import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useIsAdmin } from './useIsAdmin';
import { getApprovedAppsForUser } from '../services/appRegistryService';
import { APP_CATEGORIES, type AppCategory, type AppRegistryEntry } from '../types/appRegistry';

type AppsByCategory = Record<AppCategory, AppRegistryEntry[]>;

function emptyCategories(): AppsByCategory {
  return {
    core: [],
    productivity: [],
    community: [],
    experimental: [],
  };
}

export function useAppRegistry() {
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const [apps, setApps] = useState<AppRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApps = useCallback(async () => {
    if (!user?.email) {
      setApps([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getApprovedAppsForUser(user.email, isAdmin);
      setApps(result);
    } catch (err) {
      console.error('[useAppRegistry] Failed to fetch apps:', err);
      setError(String(err));
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email, isAdmin]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const visibleApps = apps.filter((a) => !a.globallyHidden);

  const appsByCategory: AppsByCategory = visibleApps.reduce<AppsByCategory>(
    (acc, app) => {
      const cat = APP_CATEGORIES.includes(app.category) ? app.category : 'experimental';
      acc[cat].push(app);
      return acc;
    },
    emptyCategories()
  );

  return {
    apps: visibleApps,
    appsByCategory,
    loading,
    error,
    refresh: fetchApps,
  };
}
